import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { useAllWards, useAllSubcounties, usePermission } from '../hooks';
import { colors } from '../theme/theme';
import { useSnackbar } from '../contexts/SnackbarContext';

export const WardsPage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [subcountyFilter, setSubcountyFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWard, setSelectedWard] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', subcounty_id: '' });

  const { wards, isLoading, error, createWard, updateWard, deleteWard, isCreating, isUpdating } = useAllWards({
    page: page + 1,
    per_page: rowsPerPage,
    search,
    subcounty_id: subcountyFilter !== 'all' ? subcountyFilter : undefined,
  });

  const { subcounties } = useAllSubcounties({ page: 1, per_page: 100 });

  const handleOpenDialog = (ward?: any) => {
    if (ward) {
      setSelectedWard(ward);
      setFormData({ name: ward.name || '', subcounty_id: ward.subcounty_id?.toString() || '' });
    } else {
      setSelectedWard(null);
      setFormData({ name: '', subcounty_id: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedWard(null);
    setFormData({ name: '', subcounty_id: '' });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Ward name is required', 'error');
      return;
    }
    if (!formData.subcounty_id) {
      showSnackbar('Subcounty is required', 'error');
      return;
    }

    try {
      if (selectedWard) {
        await updateWard({ id: selectedWard.id, data: { name: formData.name, subcounty_id: formData.subcounty_id } });
        showSnackbar('Ward updated successfully', 'success');
      } else {
        await createWard({ name: formData.name, subcounty_id: formData.subcounty_id });
        showSnackbar('Ward created successfully', 'success');
      }
      handleCloseDialog();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save ward';
      showSnackbar(message, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this ward?')) return;
    try {
      await deleteWard(id);
      showSnackbar('Ward deleted successfully', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to delete ward', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'subcounty',
      headerName: 'Subcounty',
      flex: 1,
      minWidth: 180,
      valueGetter: (value: any) => value?.name || 'N/A',
    },
    {
      field: 'zones_count',
      headerName: 'Zones',
      width: 120,
      valueGetter: (value) => value || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {can('locations.manage') && <IconButton size="small" onClick={() => handleOpenDialog(params.row)} sx={{ color: colors.primary.main }}>
            <Edit fontSize="small" />
          </IconButton>}
          {can('locations.manage') && <IconButton size="small" onClick={() => handleDelete(params.row.id)} sx={{ color: '#dc2626' }}>
            <Delete fontSize="small" />
          </IconButton>}
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Wards"
          subtitle="Manage wards and their administrative boundaries"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'My County', path: '/county-profile' },
            { label: 'Wards' },
          ]}
          action={
            can('locations.manage') ? <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Ward
            </Button> : undefined
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load wards. Please try again.
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2, display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search wards..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ flex: 1 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Subcounty</InputLabel>
                <Select
                  value={subcountyFilter}
                  onChange={(e) => { setSubcountyFilter(e.target.value); setPage(0); }}
                  label="Subcounty"
                >
                  <MenuItem value="all">All Subcounties</MenuItem>
                  {subcounties?.data?.map((sc: any) => (
                    <MenuItem key={sc.id} value={sc.id.toString()}>
                      {sc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={wards?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50]}
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setRowsPerPage(model.pageSize);
                  }}
                  rowCount={wards?.meta?.total || wards?.total || 0}
                  paginationMode="server"
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedWard ? 'Edit Ward' : 'Add Ward'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Subcounty</InputLabel>
                <Select
                  value={formData.subcounty_id}
                  onChange={(e) => setFormData({ ...formData, subcounty_id: e.target.value })}
                  label="Subcounty"
                >
                  {subcounties?.data?.map((subcounty: any) => (
                    <MenuItem key={subcounty.id} value={subcounty.id.toString()}>
                      {subcounty.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isCreating || isUpdating}
            >
              {(isCreating || isUpdating) ? <CircularProgress size={20} /> : (selectedWard ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
